import React, { useState } from 'react';
import styled from 'styled-components';
import PaymentHistoryTable from './PaymentHistoryTable';
import KeywordSettingTable from './KeywordSettingTable';
// PointHistoryTableContainer 모듈이 존재하지 않아서 임시로 PointHistoryTable로 대체
import PointHistoryTableContainer from './PointHistoryTableContainer';
import CustomerIpAllowlistTab from './CustomerIpAllowlistTab';
import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
} from '../styles/designTokens';

// 탭 컨테이너 스타일
const TabContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius['2xl']};
  border: 1px solid ${colors.semantic.border.primary};
  box-shadow: ${shadows.md};
  overflow: hidden;
  margin-top: ${spacing[6]};
`;

// 탭 헤더 스타일
const TabHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.semantic.border.primary};
  background: ${colors.semantic.background.secondary};
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

// 탭 버튼 스타일
const TabButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: ${spacing[4]} ${spacing[5]};
  border: none;
  background: ${props =>
    props.active ? colors.semantic.background.primary : 'transparent'};
  color: ${props =>
    props.active ? colors.semantic.text.primary : colors.semantic.text.tertiary};
  font-size: ${typography.fontSize.base};
  font-weight: ${props =>
    props.active
      ? typography.fontWeight.bold
      : typography.fontWeight.semibold};
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  border-bottom: ${props =>
    props.active
      ? `3px solid ${colors.primary[500]}`
      : '3px solid transparent'};
  position: relative;
  
  &:hover {
    background: ${props =>
      props.active
        ? colors.semantic.background.primary
        : colors.semantic.background.tertiary};
    color: ${colors.semantic.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: -2px;
  }
  
  &:not(:last-child) {
    border-right: 1px solid ${colors.semantic.border.primary};
  }
  
  @media (max-width: 768px) {
    &:not(:last-child) {
      border-right: none;
      border-bottom: 1px solid ${colors.semantic.border.primary};
    }
    
    border-bottom: ${props =>
      props.active ? `3px solid ${colors.primary[500]}` : 'none'};
    border-left: ${props =>
      props.active
        ? `3px solid ${colors.primary[500]}`
        : '3px solid transparent'};
  }
`;

// 탭 아이콘 스타일
const TabIcon = styled.span`
  margin-right: ${spacing[2]};
  font-size: ${typography.fontSize.xl};
  display: inline-block;
  transition: transform 0.2s ease;
  
  ${TabButton}:hover & {
    transform: scale(1.1);
  }
`;

// 탭 콘텐츠 스타일
const TabContent = styled.div`
  padding: ${spacing[3]};
  min-height: auto;
  background: ${colors.semantic.background.primary};
  
  @media (max-width: 768px) {
    padding: ${spacing[2]};
    min-height: auto;
  }
`;



// 탭 타입 정의
type TabType = 'payments' | 'keywords' | 'points' | 'ip';

// 탭 정보 인터페이스
interface TabInfo {
  id: TabType;
  label: string;
  icon: string;
}

// 탭 목록
const tabs: TabInfo[] = [
  { id: 'payments', label: '결제 내역', icon: '💰' },
  { id: 'keywords', label: '키워드 설정', icon: '🏷️' },
  { id: 'points', label: '포인트 내역', icon: '📊' },
  { id: 'ip', label: 'IP 관리', icon: '🛡️' },
];

// 고객 상세 탭 Props 인터페이스
interface CustomerDetailTabsProps {
  userIdx: number;
  onPointUpdate?: () => void; // 포인트 업데이트 콜백
}

// 고객 상세 탭 컴포넌트
const CustomerDetailTabs: React.FC<CustomerDetailTabsProps> = ({ userIdx, onPointUpdate }) => {
  // 활성 탭 상태 관리
  const [activeTab, setActiveTab] = useState<TabType>('payments');

  // 탭 변경 핸들러
  const handleTabChange = (tabId: TabType) => {
    setActiveTab(tabId);
  };

  // 탭 콘텐츠 렌더링
  const renderTabContent = () => {
    switch (activeTab) {
      case 'payments':
        return <PaymentHistoryTable userIdx={userIdx} />;

      case 'keywords':
        return <KeywordSettingTable userIdx={userIdx} />;

      case 'points':
        return <PointHistoryTableContainer userIdx={userIdx} onPointUpdate={onPointUpdate} />;

      case 'ip':
        return <CustomerIpAllowlistTab userIdx={userIdx} />;

      default:
        return null;
    }
  };

  return (
    <TabContainer>
      <TabHeader role="tablist" aria-label="고객 상세 정보 탭">
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            active={activeTab === tab.id}
            onClick={() => handleTabChange(tab.id)}
          >
            <TabIcon aria-hidden>{tab.icon}</TabIcon>
            {tab.label}
          </TabButton>
        ))}
      </TabHeader>

      <TabContent
        role="tabpanel"
        id={`panel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        {renderTabContent()}
      </TabContent>
    </TabContainer>
  );
};

export default CustomerDetailTabs; 